import { useEffect, useMemo, useRef, useState } from 'react'
import { SY_BOARD as boardData, PLAYER_COLOR_HEX } from '@mdev/common'
import type { Board, GameState, LogTransport, Transport } from '@mdev/common'
import { SY } from '../theme'

const board = boardData as Board

const MAP_WIDTH = 2570
const MAP_HEIGHT = 1926
const CLICK_RADIUS = 60

const TRANSPORT_COLOR: Record<Transport, string> = {
  taxi: '#FCD34D',
  bus: '#60A5FA',
  underground: '#F87171',
  ferry: '#67E8F9',
}

const TRANSPORT_WIDTH: Record<Transport, number> = {
  taxi: 3,
  bus: 5,
  underground: 7,
  ferry: 5,
}

const EDGE_RENDER_ORDER: Transport[] = [ 'taxi', 'bus', 'underground', 'ferry' ]

const PAWN_RADIUS = 52
const PAWN_HALO = 8
const REVEAL_RING_BASE = 70
const PAWN_ANIM_MS = 700

interface PawnAnimation {
  fromX: number
  fromY: number
  toX: number
  toY: number
  startedAt: number
}

type StationTier = 'taxi' | 'bus' | 'underground'

const STATION_TIER_FILL: Record<StationTier, string> = {
  taxi: '#FCD34D',
  bus: '#60A5FA',
  underground: '#F87171',
}

const STATION_TIER_TEXT: Record<StationTier, string> = {
  taxi: '#0B0F1A',
  bus: '#0B0F1A',
  underground: '#0B0F1A',
}

const stationTierById = ( () => {
  const map = new Map<number, StationTier>()
  for ( const s of board.stations ) map.set( s.id, 'taxi' )
  for ( const e of board.edges ) {
    if ( e.transport === 'bus' ) {
      if ( map.get( e.from ) === 'taxi' ) map.set( e.from, 'bus' )
      if ( map.get( e.to ) === 'taxi' ) map.set( e.to, 'bus' )
    } else if ( e.transport === 'underground' ) {
      map.set( e.from, 'underground' )
      map.set( e.to, 'underground' )
    }
  }
  return map
} )()

interface RenderOptions {
  showMap: boolean
  showEdges: boolean
  showStations: boolean
  dimMap: boolean
}

/**
 * One frame of a replay: the position of every pawn at a specific point
 * in time. When provided to BoardCanvas, the canvas renders pawns at these
 * positions instead of the live `gameState.detectives[i].station`, and
 * draws Mr. X (normally hidden) at his replay station too.
 *
 * `frameIndex` is the integer point in the timeline we're rendering (0 =
 * start, then one per Mr. X sub-move). Used purely for keying the polyline
 * highlight; pawn positions come from the maps.
 */
export interface ReplayFrame {
  frameIndex: number
  mrxStation: number
  detectiveStationsByUid: Record<string, number>
}

/**
 * The full Mr. X path for a finished game, expressed as a sequence of
 * (station, transport-into-it) pairs. Index 0 is the starting station
 * (transport=null because nobody moved into it). The canvas draws this as
 * a connected polyline, colored per transport.
 */
export interface MrxPath {
  stations: number[]
  /**
   * `transports[i]` is the transport used to arrive at `stations[i]`. By
   * convention `transports[0]` is null (it's the starting station). Length
   * matches `stations`.
   */
  transports: ( LogTransport | null )[]
}

interface Props {
  options: RenderOptions
  selectedId: number | null
  onSelect: ( id: number | null ) => void
  gameState?: GameState
  /**
   * Replay-mode override: when set, the canvas ignores the live detective
   * stations from `gameState` and renders pawns at the supplied positions,
   * including Mr. X (normally hidden). Used by `HostFinishedView` to scrub
   * through past turns. Setting `replayFrame=null` is equivalent to
   * omitting it (live mode).
   */
  replayFrame?: ReplayFrame | null
  /**
   * If set, draws Mr. X's full path as a connected polyline through the
   * supplied stations. Each segment is colored by its arrival transport.
   * Used by `HostFinishedView` in static mode to show the whole game's
   * path on a single screen.
   */
  mrxPath?: MrxPath | null
}

interface DrawState {
  options: RenderOptions
  selectedId: number | null
  gameState: GameState | undefined
  replayFrame: ReplayFrame | null
  mrxPath: MrxPath | null
  fit: number
  mapImg: HTMLImageElement | null
  mapLoaded: boolean
  animations: Map<string, PawnAnimation>
}

export function BoardCanvas( props: Props ) {
  const { options, selectedId, onSelect, gameState } = props
  const replayFrame = props.replayFrame ?? null
  const mrxPath = props.mrxPath ?? null
  const containerRef = useRef<HTMLDivElement>( null )
  const canvasRef = useRef<HTMLCanvasElement>( null )
  const mapImgRef = useRef<HTMLImageElement | null>( null )
  const [ size, setSize ] = useState( { w: 0, h: 0 } )
  const [ mapLoaded, setMapLoaded ] = useState( false )

  const stationById = useMemo( () => new Map( board.stations.map( ( s ) => [ s.id, s ] ) ), [] )

  const incidentEdges = useMemo( () => {
    if ( selectedId == null ) return []
    return board.edges.filter( ( e ) => e.from === selectedId || e.to === selectedId )
  }, [ selectedId ] )

  const neighborIds = useMemo( () => {
    const set = new Set<number>()
    for ( const e of incidentEdges ) set.add( e.from === selectedId ? e.to : e.from )
    return set
  }, [ incidentEdges, selectedId ] )

  useEffect( () => {
    const img = new Image()
    img.src = '/map.png'
    img.onload = () => {
      mapImgRef.current = img
      setMapLoaded( true )
    }
  }, [] )

  useEffect( () => {
    const el = containerRef.current
    if ( !el ) return
    const ro = new ResizeObserver( ( entries ) => {
      const r = entries[ 0 ].contentRect
      setSize( { w: r.width, h: r.height } )
    } )
    ro.observe( el )
    return () => ro.disconnect()
  }, [] )

  const fit = size.w === 0 || size.h === 0 ? 0 : Math.min( size.w / MAP_WIDTH, size.h / MAP_HEIGHT )

  const animationsRef = useRef<Map<string, PawnAnimation>>( new Map() )
  const lastStationsRef = useRef<Map<string, number>>( new Map() )

  // Build the "current pawn position" map every render. In replay mode the
  // positions come from the replay frame; in live mode they come from
  // gameState. Mr. X is keyed under the special uid '__mrx__' (only ever
  // populated in replay mode — live mode never reveals his station).
  const pawnTargets = useMemo( () => {
    const out = new Map<string, number>()
    if ( replayFrame ) {
      for ( const [ uid, station ] of Object.entries( replayFrame.detectiveStationsByUid ) ) {
        out.set( uid, station )
      }
      out.set( '__mrx__', replayFrame.mrxStation )
    } else if ( gameState ) {
      for ( const d of gameState.detectives ) {
        if ( d.station != null ) out.set( d.uid, d.station )
      }
    }
    return out
  }, [ replayFrame, gameState ] )

  useEffect( () => {
    const now = performance.now()
    // Drop animations for pawns that aren't on the board anymore.
    for ( const uid of Array.from( lastStationsRef.current.keys() ) ) {
      if ( !pawnTargets.has( uid ) ) lastStationsRef.current.delete( uid )
    }
    for ( const [ uid, station ] of pawnTargets ) {
      const prev = lastStationsRef.current.get( uid )
      if ( prev != null && prev !== station ) {
        const toS = stationById.get( station )
        if ( toS ) {
          const existing = animationsRef.current.get( uid )
          let fromX: number
          let fromY: number
          if ( existing && now - existing.startedAt < PAWN_ANIM_MS ) {
            const progress = Math.min( 1, ( now - existing.startedAt ) / PAWN_ANIM_MS )
            const eased = 1 - Math.pow( 1 - progress, 3 )
            fromX = existing.fromX + ( existing.toX - existing.fromX ) * eased
            fromY = existing.fromY + ( existing.toY - existing.fromY ) * eased
          } else {
            const prevS = stationById.get( prev )
            fromX = prevS?.x ?? toS.x
            fromY = prevS?.y ?? toS.y
          }
          animationsRef.current.set( uid, { fromX, fromY, toX: toS.x, toY: toS.y, startedAt: now } )
        }
      }
      lastStationsRef.current.set( uid, station )
    }
  }, [ pawnTargets, stationById ] )

  const drawStateRef = useRef<DrawState>( { options, selectedId, gameState, replayFrame, mrxPath, fit, mapImg: null, mapLoaded: false, animations: animationsRef.current } )
  drawStateRef.current = { options, selectedId, gameState, replayFrame, mrxPath, fit, mapImg: mapImgRef.current, mapLoaded, animations: animationsRef.current }

  useEffect( () => {
    const canvas = canvasRef.current
    if ( !canvas ) return

    let rafId = 0
    const dpr = window.devicePixelRatio || 1

    function resizeCanvas( fitVal: number ) {
      if ( !canvas || fitVal === 0 ) return false
      const renderW = MAP_WIDTH * fitVal
      const renderH = MAP_HEIGHT * fitVal
      const wantW = Math.round( renderW * dpr )
      const wantH = Math.round( renderH * dpr )
      if ( canvas.width !== wantW || canvas.height !== wantH ) {
        canvas.width = wantW
        canvas.height = wantH
        canvas.style.width = `${ renderW }px`
        canvas.style.height = `${ renderH }px`
      }
      return true
    }

    function frame( t: number ) {
      const ds = drawStateRef.current
      if ( ds.fit > 0 && resizeCanvas( ds.fit ) ) {
        const ctx = canvas!.getContext( '2d' )
        if ( ctx ) draw( ctx, ds, t, dpr, stationById, incidentEdges, neighborIds )
      }
      rafId = requestAnimationFrame( frame )
    }
    rafId = requestAnimationFrame( frame )
    return () => cancelAnimationFrame( rafId )
  }, [ stationById, incidentEdges, neighborIds ] )

  function handleClick( ev: React.MouseEvent<HTMLCanvasElement> ) {
    if ( fit === 0 ) return
    const rect = ev.currentTarget.getBoundingClientRect()
    const cssX = ev.clientX - rect.left
    const cssY = ev.clientY - rect.top
    const mapX = cssX / fit
    const mapY = cssY / fit
    let best: { id: number; d2: number } | null = null
    for ( const s of board.stations ) {
      const dx = s.x - mapX
      const dy = s.y - mapY
      const d2 = dx * dx + dy * dy
      if ( !best || d2 < best.d2 ) best = { id: s.id, d2 }
    }
    if ( best && best.d2 <= CLICK_RADIUS * CLICK_RADIUS ) {
      onSelect( best.id === selectedId ? null : best.id )
    } else {
      onSelect( null )
    }
  }

  return (
    <div
      ref={ containerRef }
      style={ {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: SY.pageBg,
      } }
    >
      <canvas
        ref={ canvasRef }
        onClick={ handleClick }
        style={ { cursor: 'pointer' } }
      />
    </div>
  )
}

/** Per-transport stroke colors used for rendering Mr. X's replay polyline. */
const PATH_COLOR: Record<LogTransport, string> = {
  taxi: '#FCD34D',
  bus: '#60A5FA',
  underground: '#F87171',
  ferry: '#67E8F9',
  black: '#E5E7EB',
}

function draw(
  ctx: CanvasRenderingContext2D,
  ds: DrawState,
  t: number,
  dpr: number,
  stationById: Map<number, Board[ 'stations' ][ number ]>,
  incidentEdges: Board[ 'edges' ],
  neighborIds: Set<number>,
) {
  const { options, selectedId, gameState, replayFrame, mrxPath, fit, mapImg, mapLoaded } = ds
  ctx.setTransform( fit * dpr, 0, 0, fit * dpr, 0, 0 )
  ctx.clearRect( 0, 0, MAP_WIDTH, MAP_HEIGHT )

  if ( options.showMap && mapLoaded && mapImg ) {
    ctx.drawImage( mapImg, 0, 0, MAP_WIDTH, MAP_HEIGHT )
    if ( options.dimMap ) {
      ctx.fillStyle = 'rgba(11, 15, 26, 0.45)'
      ctx.fillRect( 0, 0, MAP_WIDTH, MAP_HEIGHT )
    }
  }

  // Mr. X's full path polyline (static end-game view). Drawn under all
  // pawns/stations so the trace doesn't obscure interactive elements.
  if ( mrxPath && mrxPath.stations.length >= 2 ) {
    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    for ( let i = 1; i < mrxPath.stations.length; i++ ) {
      const a = stationById.get( mrxPath.stations[ i - 1 ] )
      const b = stationById.get( mrxPath.stations[ i ] )
      if ( !a || !b ) continue
      const transport = mrxPath.transports[ i ] ?? 'black'
      // White outline for legibility, then the colored stroke on top.
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.lineWidth = 18
      ctx.beginPath()
      ctx.moveTo( a.x, a.y )
      ctx.lineTo( b.x, b.y )
      ctx.stroke()
      ctx.strokeStyle = PATH_COLOR[ transport ]
      ctx.lineWidth = 12
      ctx.shadowColor = PATH_COLOR[ transport ]
      ctx.shadowBlur = 18
      ctx.beginPath()
      ctx.moveTo( a.x, a.y )
      ctx.lineTo( b.x, b.y )
      ctx.stroke()
      ctx.shadowBlur = 0
    }
    // Stop badges along the path: small numbered pills at each station.
    for ( let i = 0; i < mrxPath.stations.length; i++ ) {
      const s = stationById.get( mrxPath.stations[ i ] )
      if ( !s ) continue
      ctx.beginPath()
      ctx.arc( s.x, s.y - 38, 18, 0, Math.PI * 2 )
      ctx.fillStyle = '#0B0F1A'
      ctx.fill()
      ctx.lineWidth = 2
      ctx.strokeStyle = '#FACC15'
      ctx.stroke()
      ctx.fillStyle = '#FACC15'
      ctx.font = 'bold 18px system-ui, -apple-system, sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText( String( i ), s.x, s.y - 38 )
    }
    ctx.restore()
  }

  if ( options.showEdges ) {
    for ( const transport of EDGE_RENDER_ORDER ) {
      ctx.strokeStyle = TRANSPORT_COLOR[ transport ]
      ctx.lineWidth = TRANSPORT_WIDTH[ transport ]
      ctx.lineCap = 'round'
      ctx.setLineDash( transport === 'ferry' ? [ 12, 8 ] : [] )
      ctx.beginPath()
      for ( const e of board.edges ) {
        if ( e.transport !== transport ) continue
        const a = stationById.get( e.from )
        const b = stationById.get( e.to )
        if ( !a || !b ) continue
        ctx.moveTo( a.x, a.y )
        ctx.lineTo( b.x, b.y )
      }
      ctx.stroke()
    }
    ctx.setLineDash( [] )
  }

  if ( selectedId != null ) {
    for ( const e of incidentEdges ) {
      const a = stationById.get( e.from )
      const b = stationById.get( e.to )
      if ( !a || !b ) continue
      ctx.strokeStyle = TRANSPORT_COLOR[ e.transport ]
      ctx.lineWidth = TRANSPORT_WIDTH[ e.transport ] + 8
      ctx.lineCap = 'round'
      ctx.setLineDash( e.transport === 'ferry' ? [ 12, 8 ] : [] )
      ctx.shadowColor = TRANSPORT_COLOR[ e.transport ]
      ctx.shadowBlur = 18
      ctx.beginPath()
      ctx.moveTo( a.x, a.y )
      ctx.lineTo( b.x, b.y )
      ctx.stroke()
    }
    ctx.shadowBlur = 0
    ctx.setLineDash( [] )
  }

  // Suppress the live "Mr. X last seen" reveal marker in replay mode —
  // we're showing his actual pawn instead, which would clash visually.
  if ( !replayFrame && gameState?.mrx.lastRevealedStation != null ) {
    const s = stationById.get( gameState.mrx.lastRevealedStation )
    if ( s ) drawRevealMarker( ctx, s.x, s.y, t )
  }

  if ( options.showStations ) {
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    // In replay mode the occupied set comes from the frame; otherwise from
    // the live gameState. Includes Mr. X's station only in replay mode (so
    // his number-badge gets hidden under his pawn). Live mode never knows
    // his station so this list omits it.
    const detectiveStations = new Set<number>()
    if ( replayFrame ) {
      for ( const station of Object.values( replayFrame.detectiveStationsByUid ) ) {
        detectiveStations.add( station )
      }
      detectiveStations.add( replayFrame.mrxStation )
    } else if ( gameState ) {
      for ( const d of gameState.detectives ) {
        if ( d.station != null ) detectiveStations.add( d.station )
      }
    }
    for ( const s of board.stations ) {
      if ( detectiveStations.has( s.id ) ) continue
      const isSelected = s.id === selectedId
      const isNeighbor = neighborIds.has( s.id )
      const tier = stationTierById.get( s.id ) ?? 'taxi'
      const r = isSelected ? 28 : isNeighbor ? 24 : 20

      if ( s.isFerry ) {
        ctx.beginPath()
        ctx.arc( s.x, s.y, r + 4, 0, Math.PI * 2 )
        ctx.strokeStyle = '#67E8F9'
        ctx.lineWidth = 3
        ctx.stroke()
      }

      ctx.beginPath()
      ctx.arc( s.x, s.y, r, 0, Math.PI * 2 )
      ctx.fillStyle = isSelected ? '#FACC15' : isNeighbor ? '#34D399' : STATION_TIER_FILL[ tier ]
      ctx.fill()

      ctx.lineWidth = isSelected ? 4 : 2
      ctx.strokeStyle = isSelected ? '#FFFFFF' : '#0B0F1A'
      if ( isSelected ) {
        ctx.shadowColor = '#FACC15'
        ctx.shadowBlur = 28
      }
      ctx.stroke()
      ctx.shadowBlur = 0

      ctx.fillStyle = isSelected ? '#0B0F1A' : isNeighbor ? '#0B0F1A' : STATION_TIER_TEXT[ tier ]
      ctx.font = `bold ${ isSelected ? 22 : 18 }px system-ui, -apple-system, sans-serif`
      ctx.fillText( String( s.id ), s.x, s.y )
    }
  }

  if ( gameState ) {
    ctx.font = 'bold 56px system-ui, -apple-system, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for ( const d of gameState.detectives ) {
      // In replay mode the source-of-truth station is the frame's, not the
      // live state's. Skip the detective entirely if the frame doesn't list
      // them (shouldn't happen for valid replay payloads).
      const replayStation = replayFrame?.detectiveStationsByUid[ d.uid ]
      const station = replayFrame ? replayStation : d.station
      if ( station == null ) continue
      const s = stationById.get( station )
      if ( !s ) continue
      const awaiting = !replayFrame && gameState.awaitingUids.includes( d.uid )
      let x = s.x
      let y = s.y
      const anim = ds.animations.get( d.uid )
      if ( anim ) {
        const elapsed = t - anim.startedAt
        if ( elapsed >= PAWN_ANIM_MS ) {
          ds.animations.delete( d.uid )
        } else {
          const progress = Math.max( 0, elapsed / PAWN_ANIM_MS )
          const eased = 1 - Math.pow( 1 - progress, 3 )
          x = anim.fromX + ( anim.toX - anim.fromX ) * eased
          y = anim.fromY + ( anim.toY - anim.fromY ) * eased
        }
      }
      drawPawn( ctx, x, y, PLAYER_COLOR_HEX[ d.color ], d.avatar, awaiting, t )
    }

    // Mr. X is rendered as a pawn only in replay mode (the post-game view
    // is the only time his position can be revealed). Black background
    // with the public avatar.
    if ( replayFrame ) {
      const s = stationById.get( replayFrame.mrxStation )
      if ( s ) {
        let x = s.x
        let y = s.y
        const anim = ds.animations.get( '__mrx__' )
        if ( anim ) {
          const elapsed = t - anim.startedAt
          if ( elapsed >= PAWN_ANIM_MS ) {
            ds.animations.delete( '__mrx__' )
          } else {
            const progress = Math.max( 0, elapsed / PAWN_ANIM_MS )
            const eased = 1 - Math.pow( 1 - progress, 3 )
            x = anim.fromX + ( anim.toX - anim.fromX ) * eased
            y = anim.fromY + ( anim.toY - anim.fromY ) * eased
          }
        }
        drawPawn( ctx, x, y, '#1F2937', gameState.mrx.avatar, false, t )
      }
    }
  }
}

function drawRevealMarker( ctx: CanvasRenderingContext2D, x: number, y: number, t: number ) {
  const phase = ( t / 1500 ) % 1
  const ringR = REVEAL_RING_BASE + phase * 60
  const ringAlpha = Math.max( 0, 1 - phase )

  ctx.save()
  ctx.beginPath()
  ctx.arc( x, y, ringR, 0, Math.PI * 2 )
  ctx.strokeStyle = `rgba(250, 204, 21, ${ ringAlpha * 0.6 })`
  ctx.lineWidth = 6
  ctx.stroke()

  ctx.beginPath()
  ctx.arc( x, y, REVEAL_RING_BASE, 0, Math.PI * 2 )
  ctx.strokeStyle = '#FACC15'
  ctx.lineWidth = 6
  ctx.setLineDash( [ 14, 10 ] )
  ctx.lineDashOffset = -t / 60
  ctx.shadowColor = '#FACC15'
  ctx.shadowBlur = 30
  ctx.stroke()
  ctx.setLineDash( [] )
  ctx.shadowBlur = 0

  ctx.beginPath()
  ctx.arc( x, y, 30, 0, Math.PI * 2 )
  ctx.fillStyle = 'rgba(250, 204, 21, 0.95)'
  ctx.fill()
  ctx.lineWidth = 4
  ctx.strokeStyle = '#0B0F1A'
  ctx.stroke()
  ctx.fillStyle = '#0B0F1A'
  ctx.font = 'bold 38px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText( '?', x, y + 2 )
  ctx.restore()
}

function drawPawn( ctx: CanvasRenderingContext2D, x: number, y: number, color: string, avatar: string, awaiting: boolean, t: number ) {
  ctx.save()

  if ( awaiting ) {
    const phase = ( t / 1100 ) % 1
    const pulseR = PAWN_RADIUS + 6 + phase * 40
    const pulseAlpha = Math.max( 0, 1 - phase )
    ctx.beginPath()
    ctx.arc( x, y, pulseR, 0, Math.PI * 2 )
    ctx.strokeStyle = `rgba(252, 211, 77, ${ pulseAlpha * 0.8 })`
    ctx.lineWidth = 6
    ctx.stroke()
  }

  ctx.beginPath()
  ctx.arc( x, y + 4, PAWN_RADIUS + PAWN_HALO, 0, Math.PI * 2 )
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
  ctx.fill()

  ctx.beginPath()
  ctx.arc( x, y, PAWN_RADIUS + PAWN_HALO, 0, Math.PI * 2 )
  ctx.fillStyle = '#FFFFFF'
  ctx.fill()

  ctx.beginPath()
  ctx.arc( x, y, PAWN_RADIUS, 0, Math.PI * 2 )
  ctx.fillStyle = color
  ctx.fill()

  if ( awaiting ) {
    ctx.lineWidth = 6
    ctx.strokeStyle = '#FCD34D'
    ctx.shadowColor = '#FCD34D'
    ctx.shadowBlur = 24
    ctx.stroke()
    ctx.shadowBlur = 0
  }

  ctx.fillStyle = '#FFFFFF'
  ctx.font = 'bold 56px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText( avatar, x, y + 4 )

  ctx.restore()
}
